import type {
  ContextData,
  ContextItem,
  HistoryStackState,
  Journey,
  JourneyContextState,
  JourneyContextValue,
  Step,
  StepState,
  WithSessionIdGetter
} from '@epilot/journey-logic-commons'
import { blockController, CONTROL_NAME } from '@epilot/journey-logic-commons'
import type { ComponentProps, Dispatch, SetStateAction } from 'react'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import type { ExtendedCustomerPortalClient, BaseEntity } from '../../clients'
import { useEntityOrCustomerPortalClient } from '../../hooks'
import { makeJourneyEmptyStepValues } from '../../utils'
import {
  fetchContextEntities,
  getNeededAttributes
} from '../../utils/fetchEntityUtils'

import { getOmittedPriceComponentsPerStep } from './getOmittedPriceComponentsPerStep'
import { prepareDataForContext } from './prepareDataForContext'

type JourneyContextContextValue = WithSessionIdGetter & {
  context: JourneyContextValue
  updateContext: Dispatch<SetStateAction<JourneyContextState>>
}

const JourneyContext = createContext<JourneyContextContextValue | undefined>(
  undefined
)

export const useJourneyContext = () => {
  const context = useContext(JourneyContext)

  if (!context) {
    throw new Error(
      'useJourneyContext must be used within a JourneyContextProvider'
    )
  }

  return context
}

type JourneyContextProviderProps = WithSessionIdGetter &
  Omit<ComponentProps<typeof JourneyContext.Provider>, 'value'> & {
    /**
     * initialState is only relevant within journey builder, and can be
     * disregarded when journey app is running in standalone mode
     */
    initialState?: StepState[]
    journey: Journey
    history: HistoryStackState
    blocksDisplaySettings?: JourneyContextState['blocksDisplaySettings']
    isPreview?: boolean
    contextValues: ContextData
    shouldCheckContextValues: boolean
  }

const initialValues: Omit<
  JourneyContextState,
  '_stepsStateArray' | '_errors' | 'journeyStepStateMap'
> = {
  errorValidationMode: 'ValidateAndHide',
  _productsAdditionalAddresses: undefined,
  isAuthenticated: false,
  redeemedPromoCodes: []
}

const DEFAULT_STEP_STATE_ARRAY: Array<StepState> = []

const getStepIdFromIndex = (steps: Step[], index: number): string | undefined =>
  steps[index]?.stepId

export function JourneyContextProvider({
  journey,
  initialState,
  history: { stack, currentIndex },
  blocksDisplaySettings,
  isPreview = false,
  sessionIdGetter,
  contextValues,
  shouldCheckContextValues,
  ...props
}: JourneyContextProviderProps) {
  const [context, updateContext] = useState<JourneyContextState>({
    ...initialValues,
    _errors: Array.from({ length: journey.steps.length }, () => []),
    journeyStepStateMap: {
      [journey.journeyId]: initialState ?? makeJourneyEmptyStepValues(journey)
    }
  })

  const _stepsStateArray =
    context.journeyStepStateMap[journey.journeyId] ?? DEFAULT_STEP_STATE_ARRAY

  const productSelectionBlocksWithOptionalMappings = useMemo(
    () =>
      blockController
        .findBlocks(journey.steps, {
          type: CONTROL_NAME.PRODUCT_SELECTION_CONTROL
        })
        .filter(
          (block) => block.uischema.options?.optionalPriceComponentMappings
        ),
    [journey.steps]
  )

  const settings = journey.settings

  // Retrieve context entities
  const contextSchema = journey.contextSchema || []
  const [contextEntitiesData, setContextEntitiesData] =
    useState<Record<string, BaseEntity>>()
  const [requiredContextMissing, setRequiredContextMissing] =
    useState<Boolean>(false)

  const { client, isPortal } = useEntityOrCustomerPortalClient()

  const attributeBlocks = useMemo(
    () =>
      blockController.findBlocks(journey.steps, {
        type: CONTROL_NAME.ENTITY_ATTRIBUTE_CONTROL
      }),
    [journey.steps]
  )
  const lookupBlocks = useMemo(
    () =>
      blockController.findBlocks(journey.steps, {
        type: CONTROL_NAME.ENTITY_LOOKUP_CONTROL
      }),
    [journey.steps]
  )

  /**
   * TODO: Rewrite and move outside of block-renderers if possible
   * @see {@link https://e-pilot.atlassian.net/browse/ER-4184}
   */
  useEffect(() => {
    const neededAttributes: Record<string, string[]> = getNeededAttributes(
      lookupBlocks,
      attributeBlocks
    )

    const contextDataIsCorrect =
      !contextSchema?.length ||
      !shouldCheckContextValues ||
      (shouldCheckContextValues &&
        contextSchema.every((c: ContextItem) => {
          const key = c.paramKey.toLowerCase()
          const entityId = contextValues[key]

          return c.isRequired ? Boolean(entityId) : true
        }))

    if (!contextDataIsCorrect) {
      setRequiredContextMissing(true)
    }

    if (!isPreview && contextDataIsCorrect) {
      fetchContextEntities(
        contextSchema,
        client,
        neededAttributes,
        contextValues,
        isPortal
      )
        .then((value) => {
          setContextEntitiesData((prev) => {
            return {
              ...value,
              ...(prev?._logged_in_contact && {
                _logged_in_contact: prev._logged_in_contact
              })
            }
          })
        })
        .catch((e: Error) => {
          setRequiredContextMissing(true)
          console.error(e.message)
        })
    }
  }, [
    contextValues,
    shouldCheckContextValues,
    client,
    isPortal,
    context.isAuthenticated
  ])

  /**
   * The email of the ECP user is different from the Contact email.
   * We need this to load the logged in Contact
   * for now, this is used only for consents
   */
  useEffect(() => {
    if (isPortal) {
      const customerClient = client as ExtendedCustomerPortalClient

      customerClient.getContact().then((res) => {
        const contact = res?.data?.entity

        setContextEntitiesData((prev) => ({
          ...prev,
          _logged_in_contact: contact as BaseEntity
        }))
      })
    }
  }, [client, isPortal, context.isAuthenticated])

  const value = useMemo<JourneyContextContextValue>(() => {
    const omittedPriceComponentsPerStep = getOmittedPriceComponentsPerStep({
      stepsStateArray: _stepsStateArray,
      steps: journey.steps,
      productSelectionBlocksWithOptionalMappings:
        productSelectionBlocksWithOptionalMappings
    })

    const preparedData = prepareDataForContext(
      _stepsStateArray,
      context._journeySources,
      stack
    )

    const currentStepName = getStepIdFromIndex(journey.steps, currentIndex)

    return {
      context: {
        ...context,
        /* If we're in preview mode, always override errorValidationMode to be 'ValidateAndHide' */
        ...(isPreview && { errorValidationMode: 'ValidateAndHide' }),
        omittedPriceComponentsPerStep,
        blocksDisplaySettings,
        _stepsStateArray,
        ...preparedData,
        _navigationInfo: {
          currentStepIndex: currentIndex,
          currentStepId: currentStepName,
          stack
        },
        journey: {
          ...journey,
          settings
        },
        _isPreview: isPreview,
        _contextEntitiesData: contextEntitiesData,
        _missingContext: requiredContextMissing,
        _contextParams: contextValues
      },
      updateContext,
      sessionIdGetter
    }
  }, [
    _stepsStateArray,
    journey,
    productSelectionBlocksWithOptionalMappings,
    context,
    stack,
    currentIndex,
    isPreview,
    blocksDisplaySettings,
    settings,
    contextEntitiesData,
    requiredContextMissing,
    sessionIdGetter,
    contextValues,
    updateContext
  ])

  return <JourneyContext.Provider {...props} value={value} />
}

JourneyContextProvider.displayName = 'JourneyContextProvider'
